import { displayUsername } from '../../utils/userUtils';
import React, { useEffect, useState } from 'react';
import {
    Box, Card, CardContent, Typography, Button, Chip, Stack,
    Pagination, CircularProgress,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { format } from 'date-fns';
import { NoticeService } from '../../services/notice.service';
import { INotice } from '../../interfaces/notice.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

const PAGE_SIZE = 10;

const NoticeFeed: React.FC = () => {
    const [notices, setNotices] = useState<INotice[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const toast = useToast();

    const institutionId = AuthService.getSelectedInstitution();

    const fetchFeed = async (p: number) => {
        if (!institutionId) return;
        setLoading(true);
        try {
            const data = await NoticeService.feed(institutionId, p, PAGE_SIZE);
            setNotices(data.content);
            setTotalPages(data.totalPages);
        } catch (err) {
            toast.showApiError(err, 'Failed to load notices');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFeed(page);
    }, [page]);

    const handleMarkRead = async (id: string) => {
        try {
            await NoticeService.markRead(id);
            setNotices(ns => ns.map(n => n.id === id ? { ...n, hasCurrentUserRead: true } : n));
        } catch (err) {
            toast.showApiError(err, 'Failed to mark as read');
        }
    };

    const handleDownload = async (n: INotice) => {
        try {
            await NoticeService.downloadAttachment(n.id, n.attachmentOriginalName || 'attachment');
        } catch (err) {
            toast.showApiError(err, 'Failed to download attachment');
        }
    };

    if (loading && notices.length === 0) {
        return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }} data-help-page="notices.feed.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Notices</Typography>
            {notices.length === 0 ? (
                <Typography color="text.secondary">No notices to display.</Typography>
            ) : (
                <Stack spacing={2}>
                    {notices.map(n => (
                        <Card key={n.id} variant="outlined" sx={{ borderLeft: n.hasCurrentUserRead ? undefined : '4px solid #1976d2' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography variant="h6">{n.title}</Typography>
                                    {!n.hasCurrentUserRead && <Chip label="New" color="primary" size="small" />}
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                    Published {format(new Date(n.publishAt), 'dd MMM yyyy HH:mm')}
                                    {n.createdByName && ` · by ${displayUsername(n.createdByName)}`}
                                </Typography>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{n.body}</Typography>
                                <Stack direction="row" spacing={1}>
                                    {n.hasAttachment && (
                                        <Button size="small" startIcon={<AttachFileIcon />} data-help="notices.feed.download" onClick={() => handleDownload(n)}>
                                            {n.attachmentOriginalName || 'Download attachment'}
                                        </Button>
                                    )}
                                    {!n.hasCurrentUserRead && (
                                        <Button size="small" variant="contained" data-help="notices.feed.markRead" onClick={() => handleMarkRead(n.id)}>
                                            Mark as read
                                        </Button>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination count={totalPages} page={page + 1} onChange={(_, p) => setPage(p - 1)} />
                </Box>
            )}
        </Box>
    );
};

export default NoticeFeed;
